import { useForm, useWatch } from "react-hook-form";

import type { AssetBookingDto } from "@/api/types";
import { Form } from "@/components/Form/Form";
import { FormInput } from "@/components/Form/FormInput";
import { FormSelect } from "@/components/Form/FormSelect";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { createRequiredStringRule } from "@/helpers/rules";
import { bookableAssetMutations } from "@/queries/bookable-assets/mutations";
import { useTranslation } from "@/translations";

const FORM_ID = "decline-booking-modal-form";

type DeclineReason =
  | "double-booking"
  | "maintenance"
  | "policy-violation"
  | "emergency"
  | "priority"
  | "incorrect-info"
  | "other";

const declineReasonsIds: DeclineReason[] = [
  "double-booking",
  "maintenance",
  "policy-violation",
  "emergency",
  "priority",
  "incorrect-info",
  "other",
];

interface Reason {
  id: DeclineReason;
  label: string;
}

interface FormValues {
  reason: Reason;
  detail?: string;
}

type DeclineBookingModalProps = ModalBaseProps & {
  booking: AssetBookingDto;
};

export function DeclineBookingModal({ booking, isOpened, onOpenChange }: DeclineBookingModalProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>();

  const reason = useWatch({ control: form.control, name: "reason.id" });

  const deleteBookingMutation = bookableAssetMutations.useDeleteBooking();

  async function onSubmit(values: FormValues) {
    await deleteBookingMutation.mutateAsync({
      bookingId: booking.id,
      assetId: booking.asset.id,
      reason: values.reason.id === "other" ? values.detail : values.reason.label,
    });

    onOpenChange(false);
  }

  const declineReasons: Reason[] = declineReasonsIds.map((reason) => ({
    id: reason,
    label: t(`page.bookings.asset-detail.decline-booking.reasons.${reason}`),
  }));

  return (
    <Modal.Root
      title={t("page.bookings.asset-detail.decline-booking.title")}
      description={t("page.bookings.asset-detail.decline-booking.description")}
      size="sm"
      isNested
      {...{ isOpened, onOpenChange }}
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} fullWidth formMethods={form} onSubmit={onSubmit}>
          <FormSelect<FormValues, Reason>
            name="reason"
            placeholder={t("page.bookings.asset-detail.decline-booking.reasons.placeholder")}
            items={declineReasons}
            keySelector={(x) => x.id}
            renderOption={(x) => x.label}
            usePortal
            rules={{
              required: t("components.form.error.required", {
                inputName: t("page.bookings.asset-detail.decline-booking.reasons.placeholder"),
              }),
            }}
          />
          {reason === "other" ? (
            <FormInput<FormValues, "detail">
              name="detail"
              rules={{
                validate: {
                  required: createRequiredStringRule(
                    t,
                    "page.bookings.asset-detail.decline-booking.reasons.other.detail.name",
                  ),
                },
              }}
            />
          ) : null}
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Modal.Close>
          <Button variant="outline" tone="primary" disabled={deleteBookingMutation.isPending}>
            {t("common.action.cancel")}
          </Button>
        </Modal.Close>
        <Button
          data-testid="confirm-booking-decline-btn"
          type="submit"
          variant="filled"
          tone="warning"
          form={FORM_ID}
          isLoading={deleteBookingMutation.isPending}
        >
          {t("page.bookings.asset-detail.booking-details.cancel")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
